import axios from 'axios';
import { useCallback } from 'react';
import { TaskService } from '../service/task.service';

interface UseTaskFileUploaderResult {
  uploadTaskFiles: (filesParam: File[], email: string) => Promise<string[]>;
}

const useTaskFileUploader = (): UseTaskFileUploaderResult => {
  async function uploadTaskFile(file: File, email: string): Promise<{ ok: boolean; fileName?: string; error?: any }> {
    try {
      const presignedResponse = await TaskService.getPresignedUrl({
        mailId: email,
        key: file.name,
        contentType: file.type,
      });

      const response = presignedResponse as unknown as {
        url: string;
        fileds: Record<string, string>;
        key: string;
      };
      const { url, fileds, key: fileKey } = response;
      const formData = new FormData();

      Object.entries(fileds).forEach(([fieldKey, value]) => {
        formData.append(fieldKey, value);
      });

      formData.append('file', file);
      await axios.post(url, formData);

      // Return the S3 key path (e.g., "email@example.com/filename.jpg") for download URL construction
      // Store as "baseUrl|s3Key" format so we can reconstruct the download URL
      const s3Key = fileds.key || fileKey || file.name;
      const baseUrl = url.split('?')[0]; // Remove query params to get base URL
      const fileName = `${baseUrl}|${s3Key}`;

      return { ok: true, fileName };
    } catch (error) {
      return { ok: false, error };
    }
  }

  const uploadTaskFiles = useCallback(
    async (filesParam: File[], email: string): Promise<string[]> => {
      const filesToUpload: File[] = filesParam || [];

      if (filesToUpload.length === 0) {
        return [];
      }

      try {
        const uploadPromises = filesToUpload.map((file) => uploadTaskFile(file, email));
        const results = await Promise.all(uploadPromises);
        const successfulUploads = results.filter((r) => r.ok && r.fileName).map((r) => r.fileName!);

        return successfulUploads;
      } catch (err: unknown) {
        return [];
      }
    },
    [],
  );

  return {
    uploadTaskFiles,
  };
};

export default useTaskFileUploader;
